import { useState, type FormEvent } from "react"
import { Mail, MailCheck, CheckCircle2, ArrowRight } from "lucide-react"
import { updatePassword } from "@/lib/supabase"
import {
  MIN_PASSWORD_HINT,
  RESET_LINK_VALIDITY,
  UPDATE_PASSWORD_MESSAGES,
  validateNewPassword,
  type ResetRequest,
} from "@/lib/passwordRecovery"
import { AuthButton, AuthHeading, AuthInput, FieldHint, PasswordInput, Spinner } from "@/components/AuthUi"

/**
 * Écrans « mot de passe oublié », affichés dans le cadre de l'écran de connexion.
 * Les décisions (quel écran, quelle suite) sont dans `lib/passwordRecovery.ts`.
 */

// ============================================
// Encadré après une demande de lien (écran de connexion)
// ============================================

/**
 * « Si un compte existe » : Supabase répond « envoyé » même pour une adresse sans compte
 * (sans rien envoyer), pour qu'on ne puisse pas deviner qui est inscrit. Le texte ne doit
 * donc jamais affirmer qu'un email est parti.
 */
export function ResetRequestNotice({ request, renewed = false }: { request: ResetRequest; renewed?: boolean }) {
  return (
    <div
      role="status"
      className="mt-5 rounded-xl p-4 flex gap-3"
      style={{ background: "rgba(6,182,212,0.06)", border: "1px solid rgba(6,182,212,0.25)" }}
    >
      <MailCheck className="w-5 h-5 text-cyan-400 shrink-0 mt-0.5" />
      <div className="text-[13px] text-gray-300 leading-relaxed">
        <p>
          <span className="font-semibold text-white">Regarde ta boîte mail.</span> Si un compte existe pour{" "}
          <span className="text-white break-all">{request.email}</span>,{" "}
          {renewed ? (
            <>
              je viens de t'envoyer un nouveau lien. Il est valable {RESET_LINK_VALIDITY} et ne sert qu'une fois : ouvre
              le dernier email reçu, les précédents ne marchent plus.
            </>
          ) : (
            <>
              je viens de t'envoyer un lien pour choisir un nouveau mot de passe. Il est valable {RESET_LINK_VALIDITY} et
              ne sert qu'une fois.
            </>
          )}
        </p>
        {request.fromApp && (
          <p className="mt-2 text-gray-400">
            Le lien s'ouvrira dans ton navigateur, pas dans l'app. C'est normal : choisis ton mot de passe là-bas,
            puis reviens ici pour te connecter.
          </p>
        )}
      </div>
    </div>
  )
}

// ============================================
// Choisis ton nouveau mot de passe
// ============================================

export function NewPasswordScreen({
  email,
  onChanged,
  onSessionLost,
}: {
  email: string | null
  onChanged: () => void
  /** La session de récupération n'est plus valable : on propose un nouveau lien. */
  onSessionLost: () => void
}) {
  const [password, setPassword] = useState("")
  const [confirm, setConfirm] = useState("")
  const [visible, setVisible] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const v = validateNewPassword(password, confirm)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (!v.canSubmit || saving) return
    setSaving(true)
    setError(null)
    const { error: kind } = await updatePassword(password)
    setSaving(false)
    if (!kind) return onChanged()
    if (kind === "session") return onSessionLost()
    setError(UPDATE_PASSWORD_MESSAGES[kind])
  }

  return (
    <>
      <AuthHeading
        title="Choisis ton nouveau mot de passe"
        subtitle={email ? <>Pour le compte <span className="text-gray-300 break-all">{email}</span>.</> : undefined}
      />
      <form onSubmit={submit} className="flex flex-col gap-3">
        {/* Aide au gestionnaire de mots de passe : il enregistre le nouveau sous ce compte. */}
        {email && <input type="email" value={email} autoComplete="username" readOnly hidden />}
        <div>
          <PasswordInput
            placeholder="Nouveau mot de passe"
            value={password}
            onChange={(p) => { setPassword(p); setError(null) }}
            autoComplete="new-password"
            visible={visible}
            onToggleVisible={() => setVisible((s) => !s)}
          />
          {/* Affichée d'emblée (on choisit un mot de passe, la règle doit se voir avant),
              orange tant qu'elle n'est pas respectée — même texte qu'à l'inscription. */}
          <FieldHint tone={v.tooShort ? "warn" : "muted"}>{MIN_PASSWORD_HINT}</FieldHint>
        </div>
        <div>
          <PasswordInput
            placeholder="Confirme-le"
            value={confirm}
            onChange={(c) => { setConfirm(c); setError(null) }}
            autoComplete="new-password"
            visible={visible}
            onToggleVisible={() => setVisible((s) => !s)}
          />
          {v.mismatch && <FieldHint tone="error">Les deux mots de passe ne sont pas identiques.</FieldHint>}
        </div>

        {error && (
          <p role="alert" className="text-[13px] text-red-400 leading-relaxed">
            {error}
          </p>
        )}

        <AuthButton type="submit" disabled={!v.canSubmit || saving}>
          {saving ? (
            <>
              <Spinner />
              <span>Enregistrement…</span>
            </>
          ) : (
            <>
              <span>Enregistrer mon mot de passe</span>
              <ArrowRight className="w-4 h-4" />
            </>
          )}
        </AuthButton>
      </form>
    </>
  )
}

// ============================================
// C'est fait — lien demandé depuis l'app installée, ouvert dans le navigateur
// ============================================

export function PasswordChangedScreen({ onContinue }: { onContinue: () => void }) {
  return (
    <>
      <div className="flex justify-center mb-4">
        <CheckCircle2 className="w-10 h-10 text-cyan-400" strokeWidth={1.8} />
      </div>
      <AuthHeading title="C'est fait, ton mot de passe est changé." />
      <p className="text-[14px] text-gray-300 leading-relaxed -mt-2">
        Ce lien s'est ouvert dans ton navigateur, pas dans l'app Zeyone. Pour reprendre là où tu en étais, ouvre
        Zeyone depuis l'icône de ton écran d'accueil et connecte-toi avec ton nouveau mot de passe : ton email y est
        déjà rempli.
      </p>
      <button
        type="button"
        onClick={onContinue}
        className="w-full mt-6 py-3 rounded-xl text-[13px] font-semibold text-gray-300 cursor-pointer bg-gray-800/40 border border-gray-700/40 hover:text-white transition-colors"
      >
        Continuer ici
      </button>
    </>
  )
}

// ============================================
// Ce lien ne fonctionne plus
// ============================================

const LINK_PROBLEM_TEXT = {
  expired: {
    title: "Ce lien ne fonctionne plus",
    subtitle: `Il a expiré ou a déjà servi : un lien n'est valable qu'une fois, pendant ${RESET_LINK_VALIDITY}. Je t'en envoie un nouveau ?`,
  },
  "other-account": {
    title: "Ce lien est pour un autre compte",
    subtitle:
      "Tu es connecté ici avec un autre compte que celui du lien : je ne peux pas l'utiliser dans ce navigateur. Demande un nouveau lien, puis ouvre-le dans une fenêtre de navigation privée, ou après t'être déconnecté.",
  },
} as const

export function LinkExpiredScreen({
  reason = "expired",
  initialEmail,
  isAuthenticated,
  onSend,
  onLeave,
}: {
  reason?: keyof typeof LINK_PROBLEM_TEXT
  initialEmail: string
  isAuthenticated: boolean
  /** Envoie un nouveau lien ; renvoie l'erreur à afficher, ou null. */
  onSend: (email: string) => Promise<string | null>
  onLeave: () => void
}) {
  const [email, setEmail] = useState(initialEmail)
  const [sending, setSending] = useState(false)
  /** Adresse à laquelle le nouveau lien a été demandé, une fois la demande acceptée. */
  const [sent, setSent] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (sending) return
    if (!email.trim()) {
      setError("Saisis ton email.")
      return
    }
    setSending(true)
    setError(null)
    const err = await onSend(email)
    setSending(false)
    if (err) setError(err)
    else setSent(email.trim().toLowerCase())
  }

  return (
    <>
      <AuthHeading title={LINK_PROBLEM_TEXT[reason].title} subtitle={LINK_PROBLEM_TEXT[reason].subtitle} />

      {sent ? (
        // Même encadré que l'écran de connexion. Pas de ligne « le lien s'ouvrira dans ton
        // navigateur » : on y est déjà.
        <ResetRequestNotice request={{ email: sent, fromApp: false, at: 0 }} renewed />
      ) : (
        <form onSubmit={submit} className="flex flex-col gap-3">
          <AuthInput
            icon={Mail}
            type="email"
            placeholder="Email"
            value={email}
            onChange={(v) => { setEmail(v); setError(null) }}
            autoComplete="email"
          />
          {error && (
            <p role="alert" className="text-[13px] text-red-400 leading-relaxed">
              {error}
            </p>
          )}
          <AuthButton type="submit" disabled={sending}>
            {sending ? (
              <>
                <Spinner />
                <span>Envoi…</span>
              </>
            ) : (
              <span>M'envoyer un nouveau lien</span>
            )}
          </AuthButton>
        </form>
      )}

      <button
        type="button"
        onClick={onLeave}
        className="w-full mt-4 text-[13px] text-gray-500 hover:text-cyan-400 transition-colors cursor-pointer"
      >
        {isAuthenticated ? "Aller à mon accueil" : "Retour à la connexion"}
      </button>
    </>
  )
}
